import { memo, useState } from 'react';

/**
 * MessageBubble - Individual message display
 * Apple iMessage-inspired design with light/dark mode support
 *
 * - Own messages: Blue gradient (right-aligned)
 * - Others: Light gray / Dark gray (left-aligned)
 * - Timestamps below message
 */

function MessageBubble({ message, isOwn, showSender = true }) {
  const [showFullscreen, setShowFullscreen] = useState(false);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Check if attachment is an image
  const isImage = (attachment) => {
    if (!attachment?.type) return false;
    return attachment.type.startsWith('image/');
  };

  // Check if attachment is a video
  const isVideo = (attachment) => {
    if (!attachment?.type) return false;
    return attachment.type.startsWith('video/');
  };

  // System messages have their own styling
  if (message.type === 'system') {
    return (
      <div className="flex justify-center my-4">
        <div className="px-4 py-2 rounded-full bg-[#f5f5f7] dark:bg-white/5 text-[#86868b] dark:text-white/50 text-xs">
          {message.text}
        </div>
      </div>
    );
  }

  // Call log messages - styled bubble with call details
  if (message.type === 'call' && message.callData) {
    const { callType, status, duration } = message.callData;
    const isVideo = callType === 'video';
    const wasAnswered = status === 'ended' && duration > 0;

    // Format duration string
    const formatDuration = (seconds) => {
      if (!seconds || seconds <= 0) return null;
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      if (mins === 0) return `${secs}s`;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Get status text and colors
    const getStatusInfo = () => {
      switch (status) {
        case 'ended':
          return {
            text: wasAnswered ? formatDuration(duration) : 'Call ended',
            color: 'text-[#34c759]', // Green
            bgColor: 'bg-[#34c759]/10 dark:bg-[#34c759]/20'
          };
        case 'declined':
          return {
            text: 'Declined',
            color: 'text-[#ff3b30]', // Red
            bgColor: 'bg-[#ff3b30]/10 dark:bg-[#ff3b30]/20'
          };
        case 'missed':
          return {
            text: 'Missed',
            color: 'text-[#ff9500]', // Orange
            bgColor: 'bg-[#ff9500]/10 dark:bg-[#ff9500]/20'
          };
        case 'busy':
          return {
            text: 'Busy',
            color: 'text-[#ff9500]',
            bgColor: 'bg-[#ff9500]/10 dark:bg-[#ff9500]/20'
          };
        case 'failed':
          return {
            text: 'Failed',
            color: 'text-[#ff3b30]',
            bgColor: 'bg-[#ff3b30]/10 dark:bg-[#ff3b30]/20'
          };
        default:
          return {
            text: 'Call',
            color: 'text-[#86868b]',
            bgColor: 'bg-[#f5f5f7] dark:bg-white/10'
          };
      }
    };

    const statusInfo = getStatusInfo();

    return (
      <div className="flex justify-center my-3">
        <div className={`flex items-center gap-3 px-4 py-2.5 rounded-2xl ${statusInfo.bgColor}`}>
          {/* Call icon */}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${wasAnswered ? 'bg-[#34c759]' : 'bg-[#86868b]'}`}>
            {isVideo ? (
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            )}
          </div>

          {/* Call info */}
          <div className="flex flex-col">
            <span className="text-[#1d1d1f] dark:text-white text-sm font-medium">
              {isVideo ? 'Video call' : 'Audio call'}
            </span>
            <span className={`text-xs ${statusInfo.color}`}>
              {statusInfo.text}
            </span>
          </div>

          {/* Timestamp */}
          <span className="text-[#86868b] text-[10px] ml-2">
            {formatTime(message.timestamp)}
          </span>
        </div>
      </div>
    );
  }

  // Meeting invite messages - styled card with Join button
  if (message.type === 'meeting-invite' && message.meetingInvite) {
    const { meetingName, shareLink, accessCode, personalNote } = message.meetingInvite;

    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[85%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
          {/* Sender name (for others' messages) */}
          {!isOwn && showSender && (
            <span className="text-[#86868b] text-xs font-medium px-2">
              {message.senderName}
            </span>
          )}

          {/* Meeting invite card */}
          <div className="rounded-2xl overflow-hidden bg-white dark:bg-gradient-to-br dark:from-[#1c1c1e] dark:to-[#2c2c2e] border border-[#d2d2d7] dark:border-white/10 shadow-sm">
            {/* Header with video icon */}
            <div className="px-4 py-3 bg-gradient-to-r from-[#06c]/10 to-[#0055d4]/10 dark:from-[#06c]/20 dark:to-[#0055d4]/20 border-b border-[#d2d2d7] dark:border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#06c] flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-[#1d1d1f] dark:text-white font-medium text-sm">Meeting Invitation</p>
                  <p className="text-[#86868b] dark:text-white/60 text-xs">{meetingName}</p>
                </div>
              </div>
            </div>

            {/* Personal note if present */}
            {personalNote && (
              <div className="px-4 py-2 border-b border-[#d2d2d7]/50 dark:border-white/5">
                <p className="text-[#1d1d1f]/80 dark:text-white/80 text-sm italic">"{personalNote}"</p>
              </div>
            )}

            {/* Access code if present */}
            {accessCode && (
              <div className="px-4 py-2 flex items-center justify-between border-b border-[#d2d2d7]/50 dark:border-white/5">
                <span className="text-[#86868b] dark:text-white/60 text-xs">Access Code</span>
                <span className="text-[#1d1d1f] dark:text-white font-mono text-sm bg-[#f5f5f7] dark:bg-white/10 px-2 py-0.5 rounded">{accessCode}</span>
              </div>
            )}

            {/* Join button */}
            <div className="px-4 py-3">
              <a
                href={shareLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-[#06c] to-[#0055d4] text-white font-medium text-sm hover:opacity-90 transition-opacity"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Join Meeting
              </a>
            </div>
          </div>

          {/* Timestamp */}
          <div className="flex items-center gap-1.5 px-2">
            <span className="text-[#86868b] text-[10px]">
              {formatTime(message.timestamp)}
            </span>
            {isOwn && (
              <span className="flex items-center">
                {message.readBy && Object.keys(message.readBy).length > 1 ? (
                  <svg className="w-4 h-4 text-[#06c]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2 12l5 5L18 6" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l5 5L23 6" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 text-[#86868b]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        {/* Sender name (for others' messages) */}
        {!isOwn && showSender && (
          <span className="text-[#86868b] text-xs font-medium px-2">
            {message.senderName}
            {message.isGuest && (
              <span className="ml-1 text-[10px] text-[#86868b]">(Guest)</span>
            )}
          </span>
        )}

        {/* Message bubble */}
        <div
          className={`px-4 py-2.5 rounded-2xl ${
            isOwn
              ? 'bg-gradient-to-r from-[#06c] to-[#0055d4] text-white'
              : 'bg-[#e9e9eb] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white'
          }`}
        >
          {/* Image attachment - display inline */}
          {message.type === 'file' && message.attachment && isImage(message.attachment) && (
            <div className="mb-2">
              <img
                src={message.attachment.url}
                alt={message.attachment.name}
                className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                style={{ maxHeight: '300px', objectFit: 'contain' }}
                onClick={() => setShowFullscreen(true)}
                loading="lazy"
              />
            </div>
          )}

          {/* Video attachment - display inline */}
          {message.type === 'file' && message.attachment && isVideo(message.attachment) && (
            <div className="mb-2">
              <video
                src={message.attachment.url}
                controls
                className="max-w-full rounded-lg"
                style={{ maxHeight: '300px' }}
                preload="metadata"
              >
                Your browser does not support video playback.
              </video>
            </div>
          )}

          {/* Other file attachments - show download option */}
          {message.type === 'file' && message.attachment && !isImage(message.attachment) && !isVideo(message.attachment) && (
            <div className={`flex items-center gap-2 mb-2 pb-2 border-b ${isOwn ? 'border-white/20' : 'border-[#d2d2d7] dark:border-white/10'}`}>
              <div className={`w-8 h-8 rounded flex items-center justify-center ${isOwn ? 'bg-white/20' : 'bg-[#d2d2d7] dark:bg-white/10'}`}>
                <svg className={`w-4 h-4 ${isOwn ? 'text-white/80' : 'text-[#86868b] dark:text-white/70'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{message.attachment.name}</p>
                <p className={`text-xs ${isOwn ? 'text-white/60' : 'text-[#86868b] dark:text-white/50'}`}>
                  {(message.attachment.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <a
                href={message.attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 rounded-lg transition-colors ${isOwn ? 'hover:bg-white/20' : 'hover:bg-[#d2d2d7] dark:hover:bg-white/10'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </a>
            </div>
          )}

          {/* Message text */}
          <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">
            {message.text}
          </p>
        </div>

        {/* Timestamp and read status */}
        <div className="flex items-center gap-1.5 px-2">
          <span className="text-[#86868b] text-[10px]">
            {formatTime(message.timestamp)}
          </span>
          {/* Read status for own messages */}
          {isOwn && (
            <span className="flex items-center">
              {message.readBy && Object.keys(message.readBy).length > 1 ? (
                // Double checkmark = Read by recipient
                <svg className="w-4 h-4 text-[#06c]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2 12l5 5L18 6" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l5 5L23 6" />
                </svg>
              ) : (
                // Single checkmark = Sent/Delivered
                <svg className="w-3.5 h-3.5 text-[#86868b]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Fullscreen image viewer modal */}
      {showFullscreen && message.attachment && isImage(message.attachment) && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setShowFullscreen(false)}
        >
          {/* Close button */}
          <button
            onClick={() => setShowFullscreen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Download button */}
          <a
            href={message.attachment.url}
            download={message.attachment.name}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </a>

          {/* Full-size image */}
          <img
            src={message.attachment.url}
            alt={message.attachment.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default memo(MessageBubble);
